import { IconArrowUp, IconArrowDown } from '@tabler/icons-react';
import type { DiffWindow, StepDifference, ExpandDirection } from '~/common/test/diffTypes';
import StepDiff from './StepDiff';
import React, { useMemo } from 'react';
import { useAutoAnimate } from '~/common/hooks/useAutoAnimate';
import type { AiActionLog, AiChange } from '~/types';

export default function StepDiffsList({
  stepDifferences,
  diffWindows,
  aiChanges,
  updateDiffWindow,
  allExpanded,
  testRunAttemptId,
  aiActionLogs = [],
}: {
  stepDifferences: StepDifference[];
  diffWindows: DiffWindow[];
  aiChanges: AiChange[];
  updateDiffWindow: (diffWindowIdx: number, direction: ExpandDirection) => void;
  allExpanded?: boolean;
  testRunAttemptId?: string;
  aiActionLogs?: AiActionLog[];
}) {
  const [animationParent] = useAutoAnimate({ easing: 'ease-in-out' });

  const aiChangesByStepId = useMemo(() => Object.groupBy(aiChanges ?? [], (change) => change.stepId), [aiChanges]);

  return (
    <div
      className="flex flex-col gap-4"
      ref={animationParent}
    >
      {diffWindows.map(({ startIdx, endIdx }, diffWindowIdx) => (
        <React.Fragment key={`window-${diffWindowIdx}`}>
          {startIdx > 0 && (
            <div
              className="flex items-center"
              key={`expand-up-${diffWindowIdx}-${startIdx}`}
            >
              <div className="mr-4 w-3 text-sm text-[var(--mantine-color-dimmed)]">{startIdx}</div>
              <div
                className="flex flex-1 cursor-pointer items-center rounded bg-white/5 px-2 py-2 backdrop-blur-sm transition-colors hover:bg-white/10"
                onClick={() => updateDiffWindow(diffWindowIdx, 'up')}
              >
                <div className="flex-grow border-t border-dotted border-gray-400 dark:border-gray-600" />
                <span className="inline-flex items-center justify-center rounded-full bg-stone-200 px-3 py-1 dark:bg-gray-600">
                  <IconArrowUp
                    size={16}
                    color="black"
                  />
                </span>
                <div className="flex-grow border-t border-dotted border-gray-400 dark:border-gray-600" />
              </div>
            </div>
          )}

          {Array.from({ length: endIdx - startIdx + 1 }, (_, i) => {
            const stepIdx = startIdx + i;
            const { stepDiff, operationDiffs: currentOperationDiffs } = stepDifferences[stepIdx];

            return (
              <StepDiff
                key={`${stepDiff.new?.id}_${stepDiff.prev?.id}`}
                stepDiff={stepDiff}
                stepIndex={stepIdx}
                operationDiffs={currentOperationDiffs}
                aiChanges={aiChangesByStepId[stepDiff.prev?.id ?? ''] ?? []}
                allExpanded={allExpanded}
                testRunAttemptId={testRunAttemptId}
                aiActionLogs={aiActionLogs}
              />
            );
          })}

          {endIdx < stepDifferences.length - 1 && (
            <div
              className="flex items-center"
              key={`expand-down-${diffWindowIdx}-${endIdx}`}
            >
              <div className="mr-4 w-3 text-sm text-gray-600 dark:text-gray-400">{endIdx + 2}</div>
              <div
                className="flex flex-1 cursor-pointer items-center rounded bg-white/5 px-2 py-2 backdrop-blur-sm transition-colors hover:bg-white/10"
                onClick={() => updateDiffWindow(diffWindowIdx, 'down')}
              >
                <div className="flex-grow border-t border-dotted border-gray-400 dark:border-gray-600" />
                <span className="inline-flex items-center justify-center rounded-full bg-stone-200 px-3 py-1 dark:bg-gray-600">
                  <IconArrowDown
                    size={16}
                    color="black"
                  />
                </span>
                <div className="flex-grow border-t border-dotted border-gray-400 dark:border-gray-600" />
              </div>
            </div>
          )}
        </React.Fragment>
      ))}
    </div>
  );
}
